// SSE consumer for `GET /api/v1/conversations/:id/draft` (US-074, US-075).
//
// Mirrors the per-field consumer pattern shipped by 5b.2
// (`BusinessEditor` / `useBusinessAiField`): an imperative `start()` /
// `stop()` controller that streams deltas into local React state.
//
// Wave 6 swaps the server stub for the real LLM call; the consumer
// contract stays identical (`{ delta?, done?, body? }` frames).
//
// Wave B3 — server-side gates (US-086, US-089, P4 quota) ride as `event:
// error` SSE frames. We lift specific codes
// (`plan.ai_quota`, `conv.channel_share_locked`, `conv.off_target_blocked`)
// into a typed `error` state so `<ReviewEditor>` can render a CTA banner
// without re-parsing the envelope.

import { useCallback, useEffect, useRef, useState } from 'react';

import { useDraft } from '@barel/api-client/react';

/**
 * Recognised server-emitted error codes. Anything else falls back to
 * a generic `Error` instance so the UI surfaces a toast.
 */
export type DraftStreamErrorCode =
  | 'plan.ai_quota'
  | 'conv.channel_share_locked'
  | 'conv.off_target_blocked';

export interface DraftStreamError {
  /** Stable error code from the server envelope. */
  code: DraftStreamErrorCode | string;
  /** Optional i18n key from the server envelope. */
  i18nKey?: string;
  /** Suggested next plan when `code === 'plan.ai_quota'`. */
  nextPlan?: 'pro' | 'scale' | 'agency' | null;
}

export interface UseDraftStreamOptions {
  conversationId: string;
  /** When false, the hook never auto-runs (manual mode). */
  enabled?: boolean;
  /** Triggers a fresh stream when the dependency value changes. */
  trigger?: number;
  /** Called once when the final `done: true` frame lands. */
  onComplete?: (body: string) => void;
  /**
   * The user's current plan — used to compute `nextPlan` on a
   * `plan.ai_quota` error frame so the banner CTA can deep-link to the
   * right tier.
   */
  currentPlan?: 'discover' | 'pro' | 'scale' | 'agency';
}

export interface UseDraftStreamResult {
  /** Live-streamed body (replaced delta-by-delta). */
  body: string;
  /** True while a stream is in flight. */
  streaming: boolean;
  /** Last generic error, if any (network, unknown SSE). */
  error: Error | null;
  /** Last server-policy error, if any (quota / channel-share / off-target). */
  serverError: DraftStreamError | null;
  /** Start a fresh stream — aborts any in-flight one. */
  start: () => void;
  /** Cancel the current stream. */
  stop: () => void;
}

const PLAN_ORDER: ('discover' | 'pro' | 'scale' | 'agency')[] = [
  'discover',
  'pro',
  'scale',
  'agency',
];

function nextPlanForCurrent(
  current: 'discover' | 'pro' | 'scale' | 'agency' | undefined,
): 'pro' | 'scale' | 'agency' | null {
  if (!current) return 'pro';
  const idx = PLAN_ORDER.indexOf(current);
  if (idx < 0 || idx === PLAN_ORDER.length - 1) return null;
  return PLAN_ORDER[idx + 1] as 'pro' | 'scale' | 'agency';
}

export function useDraftStream(options: UseDraftStreamOptions): UseDraftStreamResult {
  const { conversationId, enabled = true, trigger, onComplete, currentPlan } = options;
  const draft = useDraft();
  const [body, setBody] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [serverError, setServerError] = useState<DraftStreamError | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
  }, []);

  const start = useCallback(() => {
    if (!conversationId) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBody('');
    setError(null);
    setServerError(null);
    setStreaming(true);
    void (async () => {
      let buffer = '';
      try {
        const stream = await draft.open({ conversationId, signal: controller.signal });
        for await (const frame of stream) {
          if (controller.signal.aborted) break;
          if (frame.error) {
            // Wave B3 — surface server policy gates as a typed serverError
            // instead of a generic Error so consumers can branch on `code`
            // and render the right banner / CTA.
            setServerError({
              code: frame.error.code,
              i18nKey: frame.error.i18nKey,
              nextPlan:
                frame.error.code === 'plan.ai_quota' ? nextPlanForCurrent(currentPlan) : undefined,
            });
            break;
          }
          if (frame.delta) {
            buffer += frame.delta;
            setBody(buffer);
          }
          if (frame.done) {
            const finalBody = frame.body ?? buffer;
            setBody(finalBody);
            onComplete?.(finalBody);
            break;
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
        setStreaming(false);
      }
    })();
  }, [conversationId, currentPlan, draft, onComplete]);

  // Auto-run on mount + trigger change.
  useEffect(() => {
    if (!enabled || !conversationId) return;
    start();
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // We deliberately exclude `start` so a fresh `useDraft` instance per
    // render doesn't restart the stream. The `trigger` value lets callers
    // request a re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, enabled, trigger]);

  return { body, streaming, error, serverError, start, stop };
}
